import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Badge, Input } from '@tkpod/ui';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { getCsrfToken } from '@/lib/csrf';
import { VariantOptionPresets } from './VariantOptionPresets';

/* ═══════════════════════════════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════════════════════════════ */

interface VariantOption {
  typeName: string;
  typeSlug: string;
  valueName: string;
  valueSlug: string;
  metadata: Record<string, unknown>;
}

interface VariantImage {
  type: string;
  url: string;
}

interface VariantItem {
  id: string;
  sku: string;
  supplierSku: string | null;
  gtin: string | null;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  images: VariantImage[] | null;
  cost: string;
  msrp: string | null;
  mapPrice: string | null;
  weightOz: number | null;
  heightIn: number | null;
  stockQuantity: number | null;
  trackInventory: boolean;
  continueSellingWhenOutOfStock: boolean;
  lowStockThreshold: number | null;
  isActive: boolean;
  options: VariantOption[];
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface VariantsResponse {
  data: VariantItem[];
  pagination: PaginationInfo;
}

interface AxisValue {
  name: string;
  slug: string;
  metadata: Record<string, unknown>;
}

interface OptionAxis {
  typeName: string;
  values: AxisValue[];
}

interface BulkFieldConfig {
  key: string;
  label: string;
  type: 'number' | 'text';
  step?: string;
  placeholder: string;
  parse: (v: string) => unknown;
}

type OptionFilters = Record<string, Set<string>>;

const PAGE_SIZE = 100;

const BULK_FIELDS: BulkFieldConfig[] = [
  { key: 'cost', label: 'Set Cost', type: 'number', step: '0.01', placeholder: '$0.00', parse: (v) => parseFloat(v) },
  { key: 'msrp', label: 'Set MSRP', type: 'number', step: '0.01', placeholder: '$0.00', parse: (v) => parseFloat(v) },
  { key: 'weightOz', label: 'Set Weight', type: 'number', step: '0.1', placeholder: 'oz', parse: (v) => parseFloat(v) },
  { key: 'stockQuantity', label: 'Set Stock', type: 'number', step: '1', placeholder: '0', parse: (v) => parseInt(v) },
  { key: 'supplierSku', label: 'Set Supplier SKU', type: 'text', placeholder: 'SKU', parse: (v) => v || null },
  { key: 'gtin', label: 'Set GTIN', type: 'text', placeholder: 'GTIN', parse: (v) => v || null },
];

/* ═══════════════════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════════════════ */

export function CatalogVariantMatrix({
  templateId,
  designerMode,
  productType,
  publishedProductCount = 0,
  onDirtyChange,
}: {
  templateId: string;
  designerMode?: string;
  productType?: string;
  publishedProductCount?: number;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const sheetHeight = productType === 'GANGSHEET' || productType === 'DTF_BY_SIZE';
  const bulkFields: BulkFieldConfig[] = sheetHeight
    ? [
        ...BULK_FIELDS,
        {
          key: 'heightIn',
          label: 'Set height (in)',
          type: 'number',
          step: '0.01',
          placeholder: 'in',
          parse: (value) => Number(value),
        },
      ]
    : BULK_FIELDS;
  const queryClient = useQueryClient();

  // Option presets readiness (from VariantOptionPresets child)
  const [canGenerateVariants, setCanGenerateVariants] = useState(false);
  const handleOptionCountChange = useCallback((count: number, hasValues: boolean) => {
    setCanGenerateVariants(count > 0 && hasValues);
  }, []);

  // Search (debounced)
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Pagination
  const [page, setPage] = useState(1);

  // Filters — client-side option filters; server-side search
  const [optionFilters, setOptionFilters] = useState<OptionFilters>({});

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);

  // Inline edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  // Bulk
  const [bulkField, setBulkField] = useState<BulkFieldConfig | null>(null);
  const [bulkValue, setBulkValue] = useState('');

  // Modals
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvResult, setCsvResult] = useState<CsvResult | null>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // Reset page on filter changes
  useEffect(() => { setPage(1); }, [optionFilters]);

  const isDirty = editingId !== null || bulkField !== null;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // ── Queries ──────────────────────────────────────────────────────────

  const serializedOptionFilters = useMemo(() => {
    const segments: string[] = [];
    for (const [typeName, values] of Object.entries(optionFilters)) {
      if (values.size > 0) {
        segments.push(`${typeName}:${Array.from(values).join(',')}`);
      }
    }
    return segments.length > 0 ? segments.join(';') : '';
  }, [optionFilters]);

  const variantsQueryKey = ['catalog-variants', templateId, debouncedSearch, serializedOptionFilters, page];

  const { data: variantsResponse, isLoading, isError, error } = useQuery<VariantsResponse>({
    queryKey: variantsQueryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      params.set('isActive', 'true');
      if (serializedOptionFilters) params.set('options', serializedOptionFilters);
      params.set('page', String(page));
      params.set('limit', String(PAGE_SIZE));
      const qs = params.toString();
      const res = await api.get<VariantsResponse>(
        `/api/v1/admin/catalog/${templateId}/variants?${qs}`,
      );
      return res;
    },
  });

  const variants = variantsResponse?.data ?? [];
  const pagination = variantsResponse?.pagination ?? { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 0 };

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['catalog-variants', templateId] });
  }, [queryClient, templateId]);

  const handleOptionChanged = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['upt-options', templateId] });
  }, [queryClient, templateId]);

  // ── Mutations ────────────────────────────────────────────────────────

  interface SyncResult { data: { created: number; kept: number; deleted: number; expected: number } }

  const syncVariantsMutation = useMutation({
    mutationFn: () => api.post<SyncResult>(`/api/v1/admin/catalog/${templateId}/sync-variants`, {}),
    onSuccess: (res) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['catalog-variant-axes', templateId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-variant-count', templateId] });
      queryClient.invalidateQueries({ queryKey: ['catalog-template', templateId] });
      const d = res.data;
      toast(`Variants synced: ${d.created} created, ${d.kept} kept, ${d.deleted} deleted (${d.expected} expected).`);
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to sync variants.', 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/api/v1/admin/catalog/blank-variants/${id}`, body),
    onSuccess: () => { invalidate(); setEditingId(null); toast('Variant updated.'); },
    onError: (err: Error) => { toast(err.message || 'Failed to update variant.', 'error'); },
  });

  const [uploadingVariantId, setUploadingVariantId] = useState<string | null>(null);

  const imageUploadMutation = useMutation({
    mutationFn: async ({ variantId, file }: { variantId: string; file: File }) => {
      setUploadingVariantId(variantId);
      const formData = new FormData();
      formData.append('file', file);
      const baseUrl = import.meta.env.VITE_API_URL as string;
      const headers: Record<string, string> = {};
      const csrfToken = getCsrfToken();
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
      const res = await fetch(
        `${baseUrl}/api/v1/admin/catalog/blank-variants/${variantId}/image`,
        { method: 'POST', credentials: 'include', headers, body: formData },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message ?? 'Upload failed');
      }
      return res.json();
    },
    onSettled: () => { setUploadingVariantId(null); },
    onSuccess: () => { invalidate(); toast('Image uploaded.'); },
    onError: (err: Error) => { toast(err.message || 'Image upload failed.', 'error'); },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: (body: { ids: string[]; data: Record<string, unknown> }) =>
      api.patch('/api/v1/admin/catalog/blank-variants/bulk', body),
    onSuccess: () => {
      invalidate();
      setSelectedIds(new Set());
      setSelectAllMatching(false);
      setBulkField(null);
      setBulkValue('');
      toast('Bulk update applied.');
    },
    onError: (err: Error) => { toast(err.message || 'Bulk update failed.', 'error'); },
  });

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    params.set('isActive', 'true');
    if (serializedOptionFilters) params.set('options', serializedOptionFilters);
    return params;
  }, [debouncedSearch, serializedOptionFilters]);

  const bulkUpdateAllMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => {
      const params = buildFilterParams();
      const qs = params.toString();
      return api.patch(
        `/api/v1/admin/catalog/${templateId}/variants/bulk-all${qs ? `?${qs}` : ''}`,
        body,
      );
    },
    onSuccess: () => {
      invalidate();
      setSelectedIds(new Set());
      setSelectAllMatching(false);
      setBulkField(null);
      setBulkValue('');
      toast('Bulk update applied to all matching variants.');
    },
    onError: (err: Error) => { toast(err.message || 'Bulk update failed.', 'error'); },
  });

  const csvMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const baseUrl = import.meta.env.VITE_API_URL as string;
      const headers: Record<string, string> = {};
      const csrfToken = getCsrfToken();
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
      const res = await fetch(
        `${baseUrl}/api/v1/admin/catalog/templates/${templateId}/blank-products/import-csv`,
        { method: 'POST', credentials: 'include', headers, body: formData },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Upload failed' }));
        throw new Error(err.message || 'Upload failed');
      }
      return res.json();
    },
    onSuccess: (res) => { invalidate(); setCsvResult(res.data); setCsvFile(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      api.delete(`/api/v1/admin/catalog/blank-variants/${id}`),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['upt-options', templateId] });
      toast('Variant deleted.');
    },
    onError: (err: Error) => { toast(err.message || 'Failed to delete variant.', 'error'); },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const baseUrl = import.meta.env.VITE_API_URL as string;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const csrfToken = getCsrfToken();
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
      const res = await fetch(`${baseUrl}/api/v1/admin/catalog/blank-variants/bulk`, {
        method: 'DELETE',
        credentials: 'include',
        headers,
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message ?? 'Delete failed');
      }
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['upt-options', templateId] });
      setSelectedIds(new Set());
      setSelectAllMatching(false);
      toast('Variants deleted.');
    },
    onError: (err: Error) => { toast(err.message || 'Bulk delete failed.', 'error'); },
  });

  const deleteAllMatchingMutation = useMutation({
    mutationFn: () => {
      const params = buildFilterParams();
      const qs = params.toString();
      return api.delete(
        `/api/v1/admin/catalog/${templateId}/variants/all${qs ? `?${qs}` : ''}`,
      );
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['upt-options', templateId] });
      setSelectedIds(new Set());
      setSelectAllMatching(false);
      toast('All matching variants deleted.');
    },
    onError: (err: Error) => { toast(err.message || 'Delete all failed.', 'error'); },
  });

  // ── Option axes (for filter dropdown — fetched once without option filters) ─

  const { data: optionAxes = [] } = useQuery<OptionAxis[]>({
    queryKey: ['catalog-variant-axes', templateId],
    queryFn: async () => {
      const res = await api.get<{ data: Array<{ typeName: string; values: Array<{ name: string; slug: string; metadata: Record<string, unknown> }> }> }>(
        `/api/v1/admin/catalog/${templateId}/variant-option-axes`,
      );
      return (res.data ?? []).map((axis) => ({
        typeName: axis.typeName,
        values: axis.values.map((v) => ({ name: v.name, slug: v.slug, metadata: v.metadata })),
      }));
    },
    staleTime: 60_000,
  });

  const activeFilterEntries = useMemo(() => {
    const entries: Array<{ typeName: string; valueName: string }> = [];
    for (const [typeName, values] of Object.entries(optionFilters)) {
      for (const valueName of values) {
        entries.push({ typeName, valueName });
      }
    }
    return entries;
  }, [optionFilters]);

  const hasActiveFilters = activeFilterEntries.length > 0;
  const isFiltered = hasActiveFilters || !!debouncedSearch;

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [singleDeleteTarget, setSingleDeleteTarget] = useState<VariantItem | null>(null);

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.size === 0) return;
    setShowDeleteConfirm(true);
  }, [selectedIds]);

  const confirmBulkDelete = useCallback(() => {
    if (selectAllMatching) {
      deleteAllMatchingMutation.mutate();
    } else {
      bulkDeleteMutation.mutate(Array.from(selectedIds));
    }
    setShowDeleteConfirm(false);
  }, [selectAllMatching, selectedIds, bulkDeleteMutation, deleteAllMatchingMutation]);

  // ── Filter actions ───────────────────────────────────────────────────

  const toggleOptionValue = useCallback((typeName: string, valueName: string) => {
    setOptionFilters((prev) => {
      const current = prev[typeName] ?? new Set<string>();
      const next = new Set(current);
      if (next.has(valueName)) next.delete(valueName);
      else next.add(valueName);
      const updated = { ...prev };
      if (next.size === 0) delete updated[typeName];
      else updated[typeName] = next;
      return updated;
    });
  }, []);

  const selectAllForAxis = useCallback((typeName: string, values: AxisValue[]) => {
    setOptionFilters((prev) => ({ ...prev, [typeName]: new Set(values.map((v) => v.name)) }));
  }, []);

  const clearAxisFilter = useCallback((typeName: string) => {
    setOptionFilters((prev) => {
      const updated = { ...prev };
      delete updated[typeName];
      return updated;
    });
  }, []);

  const removeFilterEntry = useCallback((typeName: string, valueName: string) => {
    setOptionFilters((prev) => {
      const current = prev[typeName];
      if (!current) return prev;
      const next = new Set(current);
      next.delete(valueName);
      const updated = { ...prev };
      if (next.size === 0) delete updated[typeName];
      else updated[typeName] = next;
      return updated;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setOptionFilters({});
    setSearch('');
    setPage(1);
  }, []);

  // ── Selection actions ────────────────────────────────────────────────

  const toggleSelect = useCallback((id: string) => {
    setSelectAllMatching(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllOnPage = useCallback(() => {
    setSelectAllMatching(false);
    const pageIds = variants.map((v) => v.id);
    setSelectedIds((prev) => {
      const allSelected = pageIds.length > 0 && pageIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        for (const id of pageIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of pageIds) next.add(id);
      return next;
    });
  }, [variants]);

  const handleSelectAllMatching = useCallback(() => {
    setSelectAllMatching(true);
    setSelectedIds(new Set(variants.map((v) => v.id)));
  }, [variants]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectAllMatching(false);
  }, []);

  // ── Inline edit ──────────────────────────────────────────────────────

  const startEdit = useCallback((v: VariantItem) => {
    setEditingId(v.id);
    setEditValues({
      cost: v.cost,
      msrp: v.msrp ?? '',
      weightOz: v.weightOz != null ? String(v.weightOz) : '',
      heightIn: v.heightIn != null ? String(v.heightIn) : '',
      supplierSku: v.supplierSku ?? '',
      gtin: v.gtin ?? '',
      stockQuantity: v.stockQuantity != null ? String(v.stockQuantity) : '',
    });
  }, []);

  const saveEdit = useCallback((id: string) => {
    const original = variants.find((v) => v.id === id);
    if (!original) return;
    const body: Record<string, unknown> = {};
    if (editValues.cost !== original.cost) body.cost = parseFloat(editValues.cost) || 0;
    if (editValues.msrp !== (original.msrp ?? '')) body.msrp = editValues.msrp ? parseFloat(editValues.msrp) : null;
    if (editValues.weightOz !== (original.weightOz != null ? String(original.weightOz) : ''))
      body.weightOz = editValues.weightOz ? parseFloat(editValues.weightOz) : null;
    if (editValues.heightIn !== (original.heightIn != null ? String(original.heightIn) : '')) {
      const height = editValues.heightIn.trim();
      if (!height) {
        body.heightIn = null;
      } else {
        const parsed = Number(height);
        if (Number.isFinite(parsed) && parsed >= 0) body.heightIn = parsed;
      }
    }
    if (editValues.supplierSku !== (original.supplierSku ?? ''))
      body.supplierSku = editValues.supplierSku || null;
    if (editValues.gtin !== (original.gtin ?? '')) body.gtin = editValues.gtin || null;
    if (editValues.stockQuantity !== (original.stockQuantity != null ? String(original.stockQuantity) : ''))
      body.stockQuantity = editValues.stockQuantity ? parseInt(editValues.stockQuantity) : null;
    if (Object.keys(body).length === 0) { setEditingId(null); return; }
    updateMutation.mutate({ id, body });
  }, [variants, editValues, updateMutation]);

  // ── Bulk actions ─────────────────────────────────────────────────────

  const effectiveSelectionCount = selectAllMatching
    ? pagination.total
    : selectedIds.size;

  const handleBulkUpdate = useCallback(() => {
    if (effectiveSelectionCount === 0 || !bulkField || !bulkValue.trim()) return;
    const parsed = bulkField.parse(bulkValue);
    if (bulkField.type === 'number' && (typeof parsed !== 'number' || Number.isNaN(parsed) || parsed < 0)) return;
    if (selectAllMatching) {
      bulkUpdateAllMutation.mutate({ [bulkField.key]: parsed });
    } else {
      bulkUpdateMutation.mutate({
        ids: Array.from(selectedIds),
        data: { [bulkField.key]: parsed },
      });
    }
  }, [effectiveSelectionCount, selectAllMatching, bulkField, bulkValue, bulkUpdateMutation, bulkUpdateAllMutation]);


  // ── Render ───────────────────────────────────────────────────────────

  const allOnPageSelected = variants.length > 0 &&
    variants.every((v) => selectedIds.has(v.id));

  return (
    <Card>
      <div className="px-6 py-4">
        {designerMode === 'NONE' && publishedProductCount > 0 ? (
          <p className="mb-4 rounded-md border border-border bg-surface-secondary px-4 py-3 text-sm text-text-secondary">
            Published Shopify products do not pick up new variants automatically. Republish from the merchant app after saving variants here.
          </p>
        ) : null}
        {/* Variant Option Presets */}
        <VariantOptionPresets
          templateId={templateId}
          onOptionChanged={handleOptionChanged}
          onOptionCountChange={handleOptionCountChange}
        />

        <div className="my-4 flex items-center gap-3">
          <div className="flex-1 border-t border-border" />
          <Button
            size="sm"
            onClick={() => syncVariantsMutation.mutate()}
            disabled={syncVariantsMutation.isPending || !canGenerateVariants}
            title={!canGenerateVariants ? 'Add at least one option type with values first' : undefined}
          >
            {syncVariantsMutation.isPending ? 'Syncing...' : 'Save & Generate Variants'}
          </Button>
          <div className="flex-1 border-t border-border" />
        </div>

        {/* Variant Table Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wider">Variant Table</h3>
            <p className="text-xs text-text-tertiary mt-0.5">
              {pagination.total} total
              {isFiltered && ` · ${variants.length} on this page`}
            </p>
            {sheetHeight ? (
              <p className="mt-1 text-xs text-text-tertiary">
                Gang sheet variant matching reads Height (in). The print size is the trimmed image, and the sheet width is the maximum.
              </p>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowCsvImport(true)}>
              Import CSV
            </Button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="min-w-[180px] max-w-[240px]">
            <Input
              placeholder="Search SKU or option values..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {optionAxes.map((axis) => (
            <FilterDropdown
              key={axis.typeName}
              axis={axis}
              selected={optionFilters[axis.typeName] ?? new Set()}
              onToggle={(val) => toggleOptionValue(axis.typeName, val)}
              onSelectAll={() => selectAllForAxis(axis.typeName, axis.values)}
              onClear={() => clearAxisFilter(axis.typeName)}
            />
          ))}

          <Button size="sm" variant="ghost" onClick={() => invalidate()} className="ml-auto">
            <svg className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" />
            </svg>
            Refresh
          </Button>
        </div>

        {/* Active filter badges */}
        {hasActiveFilters && (
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            {activeFilterEntries.map((entry) => {
              const axis = optionAxes.find((a) => a.typeName === entry.typeName);
              const val = axis?.values.find((v) => v.name === entry.valueName);
              const hex = (val?.metadata as Record<string, string>)?.hexCode;
              return (
                <span
                  key={`${entry.typeName}:${entry.valueName}`}
                  className="inline-flex items-center gap-1 rounded-full bg-brand-50 border border-brand-200 pl-2 pr-1 py-0.5 text-xs font-medium text-brand-700"
                >
                  {hex && (
                    <span className="inline-block h-2 w-2 rounded-full border border-brand-300" style={{ backgroundColor: hex }} />
                  )}
                  <span className="text-brand-500">{entry.typeName}:</span>
                  {entry.valueName}
                  <button
                    type="button"
                    onClick={() => removeFilterEntry(entry.typeName, entry.valueName)}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-brand-100 transition-colors"
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              );
            })}
            <button
              type="button"
              onClick={clearAllFilters}
              className="text-xs font-medium text-brand-600 hover:text-brand-700 ml-1"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Select-all-filtered banner — shown when filters are active but nothing selected */}
        {isFiltered && variants.length > 0 && selectedIds.size === 0 && (
          <div className="mb-3 flex items-center gap-3 rounded-lg border border-warning-300 bg-warning-50 px-4 py-2.5">
            <svg className="h-4 w-4 shrink-0 text-warning-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
            </svg>
            <span className="text-sm text-warning-700">
              <span className="font-semibold">{pagination.total}</span> variants match your filters.
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={handleSelectAllMatching}
              className="border-warning-300 bg-white text-warning-700 hover:bg-warning-100"
            >
              Select all {pagination.total} filtered variants
            </Button>
          </div>
        )}

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="mb-3 rounded-lg border border-brand-200 bg-brand-50/50 px-4 py-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {/* Selection count & expand to all */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-brand-700">
                  {selectAllMatching
                    ? `All ${pagination.total} variants selected`
                    : `${selectedIds.size} selected`}
                </span>
                {allOnPageSelected && !selectAllMatching && pagination.total > variants.length && (
                  <button
                    type="button"
                    onClick={handleSelectAllMatching}
                    className="text-xs font-medium text-brand-600 underline decoration-brand-300 hover:text-brand-700"
                  >
                    Select all {pagination.total} matching
                  </button>
                )}
              </div>

              <div className="h-4 w-px bg-brand-200" />

              {/* Field selector pills */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-text-tertiary mr-0.5">Field:</span>
                {bulkFields.map((field) => (
                  <button
                    key={field.key}
                    type="button"
                    onClick={() => { setBulkField(bulkField?.key === field.key ? null : field); setBulkValue(''); }}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                      bulkField?.key === field.key
                        ? 'bg-brand-600 text-white shadow-sm'
                        : 'bg-white text-text-secondary border border-border hover:bg-surface-secondary hover:border-brand-300'
                    }`}
                  >
                    {field.label}
                  </button>
                ))}
              </div>

              {/* Value input + Apply (shown inline when a field is selected) */}
              {bulkField && (
                <>
                  <div className="h-4 w-px bg-brand-200" />
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-text-tertiary">Value:</span>
                    <input
                      type={bulkField.type}
                      step={bulkField.step}
                      min={bulkField.type === 'number' ? '0' : undefined}
                      value={bulkValue}
                      onChange={(e) => setBulkValue(e.target.value)}
                      placeholder={bulkField.placeholder}
                      className="w-28 rounded-md border border-border bg-white px-2.5 py-1.5 text-xs shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                      autoFocus
                    />
                    <Button
                      size="sm"
                      onClick={handleBulkUpdate}
                      disabled={!bulkValue.trim() || bulkUpdateMutation.isPending || bulkUpdateAllMutation.isPending}
                    >
                      {(bulkUpdateMutation.isPending || bulkUpdateAllMutation.isPending)
                        ? 'Applying...'
                        : `Apply to ${effectiveSelectionCount}`}
                    </Button>
                  </div>
                </>
              )}

              {/* Delete + Clear */}
              <div className="flex items-center gap-1.5 ml-auto">
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteMutation.isPending || deleteAllMatchingMutation.isPending}
                  className="rounded-md border border-danger-300 bg-white px-2.5 py-1 text-xs font-medium text-danger-600 hover:bg-danger-50 transition-colors disabled:opacity-50"
                >
                  {(bulkDeleteMutation.isPending || deleteAllMatchingMutation.isPending)
                    ? 'Deleting...'
                    : `Delete ${effectiveSelectionCount}`}
                </button>
                <div className="h-4 w-px bg-brand-200 mx-1" />
                <button
                  type="button"
                  onClick={clearSelection}
                  className="rounded-md px-2 py-1 text-xs font-medium text-text-tertiary hover:text-text-primary hover:bg-surface-secondary transition-colors"
                >
                  Clear selection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error banners */}
        {(bulkUpdateMutation.isError || bulkUpdateAllMutation.isError) && (
          <div className="mb-3 rounded-md bg-danger-50 border border-danger-200 p-3 text-sm text-danger-700">
            {(bulkUpdateMutation.error as Error)?.message
              || (bulkUpdateAllMutation.error as Error)?.message
              || 'Bulk update failed.'}
          </div>
        )}
        {(bulkDeleteMutation.isError || deleteAllMatchingMutation.isError) && (
          <div className="mb-3 rounded-md bg-danger-50 border border-danger-200 p-3 text-sm text-danger-700">
            {(bulkDeleteMutation.error as Error)?.message
              || (deleteAllMatchingMutation.error as Error)?.message
              || 'Delete operation failed.'}
          </div>
        )}

        {/* Table */}
        {isLoading ? (
          <div className="animate-pulse">
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-secondary/50">
                    <th className="px-3 py-2.5 w-10"><div className="h-4 w-4 rounded bg-surface-tertiary" /></th>
                    <th className="px-3 py-2.5 w-14"><div className="h-4 w-10 rounded bg-surface-tertiary" /></th>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <th key={i} className="px-3 py-2.5"><div className="h-4 w-16 rounded bg-surface-tertiary" /></th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2"><div className="h-4 w-4 rounded bg-surface-tertiary" /></td>
                      <td className="px-3 py-2"><div className="h-8 w-8 rounded bg-surface-tertiary" /></td>
                      {[1, 2, 3, 4, 5, 6].map((j) => (
                        <td key={j} className="px-3 py-2"><div className="h-4 w-full rounded bg-surface-tertiary" /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : isError ? (
          <div className="py-12 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-danger-50">
              <svg className="h-5 w-5 text-danger-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-text-secondary">Failed to load variants</p>
            <p className="mt-1 text-xs text-text-tertiary">{(error as Error)?.message || 'An unexpected error occurred.'}</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => invalidate()}>
              Retry
            </Button>
          </div>
        ) : variants.length === 0 ? (
          <div className="py-12 text-center">
            {pagination.total === 0 ? (
              <>
                <svg className="mx-auto h-10 w-10 text-text-tertiary/50" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m6 4.125l2.25 2.25m0 0l2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                </svg>
                <p className="mt-3 text-sm font-medium text-text-secondary">No variants generated</p>
                <p className="mt-1 text-xs text-text-tertiary">Assign options first, then generate variants.</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-text-secondary">No variants match your filters</p>
                <button type="button" onClick={clearAllFilters} className="mt-2 text-xs font-medium text-brand-600 hover:text-brand-700">
                  Clear all filters
                </button>
              </>
            )}
          </div>
        ) : (
          <VariantTable
            sheetHeight={sheetHeight}
            variants={variants}
            totalCount={pagination.total}
            optionAxes={optionAxes}
            selectedIds={selectedIds}
            toggleSelect={toggleSelect}
            toggleAll={toggleAllOnPage}
            allSelected={allOnPageSelected}
            editingId={editingId}
            editValues={editValues}
            setEditValues={setEditValues}
            startEdit={startEdit}
            saveEdit={saveEdit}
            setEditingId={setEditingId}
            isSaving={updateMutation.isPending}
            onUploadImage={(variantId, file) => imageUploadMutation.mutate({ variantId, file })}
            uploadingVariantId={uploadingVariantId}
            onDelete={(v) => setSingleDeleteTarget(v)}
            deletingId={deleteMutation.isPending ? (deleteMutation.variables ?? null) : null}
          />
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <PaginationControls
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={pagination.limit}
            onPageChange={(p) => { setPage(p); setEditingId(null); setSelectedIds(new Set()); setSelectAllMatching(false); }}
          />
        )}
      </div>

      {/* Modals */}
      {showCsvImport && (
        <CsvImportModal
          templateId={templateId}
          csvInputRef={csvInputRef}
          csvFile={csvFile}
          setCsvFile={setCsvFile}
          csvResult={csvResult}
          setCsvResult={setCsvResult}
          csvMutation={csvMutation}
          onClose={() => { setShowCsvImport(false); setCsvResult(null); setCsvFile(null); }}
        />
      )}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-lg bg-surface shadow-xl">
            <div className="px-6 py-5 space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger-50">
                  <svg className="h-5 w-5 text-danger-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">Delete variants</h3>
                  <p className="mt-1 text-sm text-text-secondary">
                    {selectAllMatching
                      ? `This will permanently delete all ${pagination.total} matching variants.`
                      : `This will permanently delete ${selectedIds.size} selected variant${selectedIds.size === 1 ? '' : 's'}.`}
                    {' '}This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <button
                  type="button"
                  onClick={confirmBulkDelete}
                  className="rounded-md bg-danger-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-danger-700 transition-colors"
                >
                  Delete {effectiveSelectionCount} variant{effectiveSelectionCount === 1 ? '' : 's'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {singleDeleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-lg bg-surface shadow-xl">
            <div className="px-6 py-5 space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger-50">
                  <svg className="h-5 w-5 text-danger-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">Delete variant</h3>
                  <p className="mt-1 text-sm text-text-secondary">
                    Delete variant <strong className="text-text-primary">{singleDeleteTarget.sku}</strong>? This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setSingleDeleteTarget(null)}>
                  Cancel
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    deleteMutation.mutate(singleDeleteTarget.id);
                    setSingleDeleteTarget(null);
                  }}
                  className="rounded-md bg-danger-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-danger-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   PaginationControls
   ═══════════════════════════════════════════════════════════════════════════ */

function PaginationControls({
  page,
  totalPages,
  total,
  limit,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
}) {
  const rangeStart = (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);

  const pageNumbers = useMemo(() => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('ellipsis');
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  }, [page, totalPages]);

  return (
    <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
      <p className="text-xs text-text-tertiary">
        {rangeStart}–{rangeEnd} of {total}
      </p>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="rounded-md border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-surface-secondary disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        {pageNumbers.map((p, idx) =>
          p === 'ellipsis' ? (
            <span key={`e${idx}`} className="px-1 text-xs text-text-tertiary">...</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                p === page
                  ? 'bg-brand-600 text-white'
                  : 'text-text-secondary hover:bg-surface-secondary'
              }`}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="rounded-md border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-surface-secondary disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FilterDropdown
   ═══════════════════════════════════════════════════════════════════════════ */

function FilterDropdown({
  axis,
  selected,
  onToggle,
  onSelectAll,
  onClear,
}: {
  axis: OptionAxis;
  selected: Set<string>;
  onToggle: (valueName: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setFilterText('');
      }
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); setFilterText(''); }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [open]);

  const filteredValues = filterText
    ? axis.values.filter((v) => v.name.toLowerCase().includes(filterText.toLowerCase()))
    : axis.values;

  const count = selected.size;
  const allSelected = count === axis.values.length && count > 0;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
          count > 0
            ? 'border-brand-300 bg-brand-50 text-brand-700'
            : 'border-border bg-surface text-text-secondary hover:bg-surface-secondary'
        }`}
      >
        {axis.typeName}
        {count > 0 && (
          <span className="inline-flex items-center justify-center rounded-full bg-brand-600 text-white text-[10px] font-bold h-4 min-w-[16px] px-1">
            {count}
          </span>
        )}
        <svg className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-56 rounded-lg border border-border bg-surface shadow-lg">
          {axis.values.length > 6 && (
            <div className="border-b border-border p-2">
              <input
                type="text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                placeholder={`Search ${axis.typeName.toLowerCase()}...`}
                className="w-full rounded-md border border-border bg-surface-secondary px-2.5 py-1.5 text-xs placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-brand-500"
                autoFocus
              />
            </div>
          )}

          <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
            <button
              type="button"
              onClick={() => { if (allSelected) { onClear(); } else { onSelectAll(); } }}
              className="text-[11px] font-medium text-brand-600 hover:text-brand-700"
            >
              {allSelected ? 'Deselect all' : 'Select all'}
            </button>
            {count > 0 && (
              <button
                type="button"
                onClick={onClear}
                className="text-[11px] font-medium text-text-tertiary hover:text-text-primary"
              >
                Clear
              </button>
            )}
          </div>

          <div className="max-h-56 overflow-y-auto py-1">
            {filteredValues.length === 0 ? (
              <p className="px-3 py-2 text-xs text-text-tertiary">No matches</p>
            ) : (
              filteredValues.map((val) => {
                const hex = (val.metadata as Record<string, string>)?.hexCode;
                const isChecked = selected.has(val.name);
                return (
                  <button
                    key={val.slug}
                    type="button"
                    onClick={() => onToggle(val.name)}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
                      isChecked ? 'bg-brand-50/50' : 'hover:bg-surface-secondary'
                    }`}
                  >
                    <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors ${
                      isChecked ? 'border-brand-600 bg-brand-600' : 'border-border bg-surface'
                    }`}>
                      {isChecked && (
                        <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                      )}
                    </span>
                    {hex && (
                      <span className="inline-block h-3 w-3 shrink-0 rounded-full border border-border" style={{ backgroundColor: hex }} />
                    )}
                    <span className={`truncate ${isChecked ? 'font-medium text-text-primary' : 'text-text-secondary'}`}>
                      {val.name}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   VariantTable
   ═══════════════════════════════════════════════════════════════════════════ */

function VariantTable({
  sheetHeight,
  variants,
  totalCount,
  optionAxes,
  selectedIds,
  toggleSelect,
  toggleAll,
  allSelected,
  editingId,
  editValues,
  setEditValues,
  startEdit,
  saveEdit,
  setEditingId,
  isSaving,
  onUploadImage,
  uploadingVariantId,
  onDelete,
  deletingId,
}: {
  sheetHeight: boolean;
  variants: VariantItem[];
  totalCount: number;
  optionAxes: OptionAxis[];
  selectedIds: Set<string>;
  toggleSelect: (id: string) => void;
  toggleAll: () => void;
  allSelected: boolean;
  editingId: string | null;
  editValues: Record<string, string>;
  setEditValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  startEdit: (v: VariantItem) => void;
  saveEdit: (id: string) => void;
  setEditingId: (id: string | null) => void;
  isSaving: boolean;
  onUploadImage: (variantId: string, file: File) => void;
  uploadingVariantId: string | null;
  onDelete: (v: VariantItem) => void;
  deletingId: string | null;
}) {
  const optionTypeNames = optionAxes.map((a) => a.typeName);

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-surface-secondary/50 text-left text-text-secondary">
            <th className="px-3 py-2.5 w-10">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && !allSelected; }}
                  onChange={toggleAll}
                  className="rounded border-border"
                />
                {variants.length !== totalCount && (
                  <span className="text-[10px] text-text-tertiary font-normal">
                    {variants.length}/{totalCount}
                  </span>
                )}
              </div>
            </th>
            <th className="px-3 py-2.5 font-medium text-xs w-14">Image</th>
            <th className="px-3 py-2.5 font-medium text-xs">SKU</th>
            {optionTypeNames.map((name) => (
              <th key={name} className="px-3 py-2.5 font-medium text-xs">{name}</th>
            ))}
            <th className="px-3 py-2.5 font-medium text-xs">Cost</th>
            <th className="px-3 py-2.5 font-medium text-xs">MSRP</th>
            <th className="px-3 py-2.5 font-medium text-xs">Weight (oz)</th>
            {sheetHeight ? <th className="px-3 py-2.5 font-medium text-xs">Height (in)</th> : null}
            <th className="px-3 py-2.5 font-medium text-xs">GTIN</th>
            <th className="px-3 py-2.5 font-medium text-xs">Supplier SKU</th>
            <th className="px-3 py-2.5 font-medium text-xs">Stock</th>
            <th className="px-3 py-2.5 font-medium text-xs">Status</th>
            <th className="px-3 py-2.5 font-medium text-xs w-20" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {variants.map((v) => {
            const isEditing = editingId === v.id;
            const isSelected = selectedIds.has(v.id);
            return (
              <tr
                key={v.id}
                className={`transition-colors ${
                  isSelected ? 'bg-brand-50/30' : 'hover:bg-surface-secondary/30'
                } ${!v.isActive ? 'opacity-60' : ''}`}
              >
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(v.id)}
                    className="rounded border-border"
                  />
                </td>
                <td className="px-3 py-2">
                  <VariantImageCell
                    variant={v}
                    onUpload={(file) => onUploadImage(v.id, file)}
                    isUploading={uploadingVariantId === v.id}
                  />
                </td>
                <td className="px-3 py-2 font-mono text-xs text-text-primary">{v.sku}</td>
                {optionTypeNames.map((name) => (
                  <td key={name} className="px-3 py-2">
                    <OptionCell variant={v} typeName={name} />
                  </td>
                ))}
                {isEditing ? (
                  <>
                    <EditCell value={editValues.cost} field="cost" type="number" step="0.01" setEditValues={setEditValues} width="w-20" />
                    <EditCell value={editValues.msrp} field="msrp" type="number" step="0.01" setEditValues={setEditValues} width="w-20" />
                    <EditCell value={editValues.weightOz} field="weightOz" type="number" step="0.1" setEditValues={setEditValues} width="w-16" />
                    {sheetHeight ? (
                      <EditCell value={editValues.heightIn ?? ''} field="heightIn" type="number" step="0.01" setEditValues={setEditValues} width="w-16" placeholder="in" />
                    ) : null}
                    <EditCell value={editValues.gtin} field="gtin" type="text" setEditValues={setEditValues} width="w-24" placeholder="GTIN" />
                    <EditCell value={editValues.supplierSku} field="supplierSku" type="text" setEditValues={setEditValues} width="w-24" placeholder="Supplier SKU" />
                    <EditCell value={editValues.stockQuantity} field="stockQuantity" type="number" setEditValues={setEditValues} width="w-16" />
                    <td className="px-3 py-2">
                      <Badge variant={v.isActive ? 'success' : 'default'} size="sm">
                        {v.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => saveEdit(v.id)}
                          disabled={isSaving}
                          className="rounded bg-brand-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                        >
                          {isSaving ? '...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          className="rounded px-2 py-1 text-[11px] font-medium text-text-secondary hover:bg-surface-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2 text-text-primary">${v.cost}</td>
                    <td className="px-3 py-2">{v.msrp ? `$${v.msrp}` : <Dash />}</td>
                    <td className="px-3 py-2">{v.weightOz != null ? v.weightOz : <Dash />}</td>
                    {sheetHeight ? <td className="px-3 py-2">{v.heightIn != null ? v.heightIn : <Dash />}</td> : null}
                    <td className="px-3 py-2 font-mono text-xs">{v.gtin ?? <Dash />}</td>
                    <td className="px-3 py-2 font-mono text-xs">{v.supplierSku ?? <Dash />}</td>
                    <td className="px-3 py-2">{v.stockQuantity != null ? v.stockQuantity : <Dash />}</td>
                    <td className="px-3 py-2">
                      <Badge variant={v.isActive ? 'success' : 'default'} size="sm">
                        {v.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => startEdit(v)}
                          className="rounded px-2 py-1 text-[11px] font-medium text-brand-600 hover:bg-brand-50 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(v)}
                          disabled={deletingId === v.id}
                          className="rounded px-2 py-1 text-[11px] font-medium text-danger-500 hover:bg-danger-50 transition-colors disabled:opacity-50"
                        >
                          {deletingId === v.id ? '...' : 'Del'}
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Shared components
   ═══════════════════════════════════════════════════════════════════════════ */

function Dash() {
  return <span className="text-text-tertiary">—</span>;
}

function EditCell({
  value,
  field,
  type,
  step,
  setEditValues,
  width,
  placeholder,
}: {
  value: string;
  field: string;
  type: string;
  step?: string;
  setEditValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  width: string;
  placeholder?: string;
}) {
  return (
    <td className="px-3 py-2">
      <input
        type={type}
        step={step}
        value={value}
        onChange={(e) => setEditValues((prev) => ({ ...prev, [field]: e.target.value }))}
        className={`${width} rounded border border-border bg-surface px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500`}
        placeholder={placeholder}
      />
    </td>
  );
}

function OptionCell({ variant, typeName }: { variant: VariantItem; typeName: string }) {
  const opt = variant.options.find((o) => o.typeName === typeName);
  if (!opt) return <span className="text-text-tertiary">—</span>;

  const hex = (opt.metadata as Record<string, string>)?.hexCode;
  return (
    <div className="flex items-center gap-1.5">
      {hex && (
        <span
          className="inline-block h-3 w-3 shrink-0 rounded-full border border-border"
          style={{ backgroundColor: hex }}
        />
      )}
      <span className="text-text-primary">{opt.valueName}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   VariantImageCell
   ═══════════════════════════════════════════════════════════════════════════ */

function VariantImageCell({
  variant,
  onUpload,
  isUploading,
}: {
  variant: VariantItem;
  onUpload: (file: File) => void;
  isUploading: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const src = variant.thumbnailUrl ?? variant.imageUrl;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
      e.target.value = '';
    }
  };

  return (
    <button
      type="button"
      title={src ? 'Change image' : 'Upload image'}
      onClick={() => inputRef.current?.click()}
      disabled={isUploading}
      className="group relative block"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={handleFileChange}
        className="hidden"
      />
      {src ? (
        <div className="relative">
          <img
            src={src}
            alt={variant.sku}
            className="h-8 w-8 rounded border border-border object-cover"
          />
          <div className={`absolute inset-0 flex items-center justify-center rounded transition-colors ${isUploading ? 'bg-black/50' : 'bg-black/0 group-hover:bg-black/40'}`}>
            {isUploading ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <svg className="h-3.5 w-3.5 text-white opacity-0 transition-opacity group-hover:opacity-100" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Z" />
              </svg>
            )}
          </div>
        </div>
      ) : (
        <div className="flex h-8 w-8 items-center justify-center rounded border border-dashed border-border bg-surface-secondary transition-colors group-hover:border-brand-400 group-hover:bg-brand-50">
          {isUploading ? (
            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          ) : (
            <svg className="h-3.5 w-3.5 text-text-tertiary/50 group-hover:text-brand-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          )}
        </div>
      )}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CSV Import Modal
   ═══════════════════════════════════════════════════════════════════════════ */

// ═══════════════════════════════════════════════════════════════════════════
// CSV Import Modal
// ═══════════════════════════════════════════════════════════════════════════

interface CsvResult {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

function CsvImportModal({
  templateId,
  csvInputRef,
  csvFile,
  setCsvFile,
  csvResult,
  setCsvResult,
  csvMutation,
  onClose,
}: {
  templateId: string;
  csvInputRef: React.RefObject<HTMLInputElement | null>;
  csvFile: File | null;
  setCsvFile: (f: File | null) => void;
  csvResult: CsvResult | null;
  setCsvResult: (r: CsvResult | null) => void;
  csvMutation: { mutate: (file: File) => void; isPending: boolean; isError: boolean; error: Error | null };
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h3 className="text-base font-semibold text-text-primary">Import Variants from CSV</h3>
          <button type="button" onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-text-secondary">
            Upload a CSV file with required columns: <span className="font-medium text-text-primary">brand, model, name, sku, cost</span>.
            Optional: msrp, weight_oz.
          </p>
          <div
            onClick={() => csvInputRef.current?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors ${
              csvFile ? 'border-success-300 bg-success-50' : 'border-border hover:border-brand-300 hover:bg-brand-50/50'
            }`}
          >
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) { setCsvFile(f); setCsvResult(null); } }}
              className="hidden"
            />
            {csvFile ? (
              <div className="text-center">
                <p className="text-sm font-medium text-success-700">{csvFile.name}</p>
                <p className="text-xs text-success-600">{(csvFile.size / 1024).toFixed(1)} KB</p>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setCsvFile(null); setCsvResult(null); }}
                  className="mt-1 text-xs text-text-tertiary underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <p className="text-sm text-text-secondary">Click to select a CSV file</p>
            )}
          </div>
          {csvResult && (
            <div className={`rounded-lg border p-4 text-sm ${
              csvResult.errors.length > 0 ? 'border-warning-300 bg-warning-50' : 'border-success-300 bg-success-50'
            }`}>
              <p className="font-semibold">
                {csvResult.errors.length > 0 ? 'Completed with warnings' : 'Import complete'}
              </p>
              <div className="mt-2 flex gap-4 text-xs font-medium">
                <span>Created: {csvResult.created}</span>
                <span>Updated: {csvResult.updated}</span>
                <span>Skipped: {csvResult.skipped}</span>
              </div>
              {csvResult.errors.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-xs opacity-80">
                  {csvResult.errors.slice(0, 5).map((err, i) => (
                    <li key={i}>{'\u2022'} {err}</li>
                  ))}
                  {csvResult.errors.length > 5 && (
                    <li>...and {csvResult.errors.length - 5} more</li>
                  )}
                </ul>
              )}
            </div>
          )}
          {csvMutation.isError && (
            <div className="rounded-lg border border-danger-300 bg-danger-50 p-4 text-sm text-danger-700">
              {csvMutation.error?.message || 'Import failed.'}
            </div>
          )}
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs font-medium text-brand-600 transition-colors hover:text-brand-700"
              onClick={() => {
                const headers = 'brand,model,name,sku,cost,msrp,weight_oz';
                const example = 'Bella+Canvas,3001,Unisex Jersey Tee,BC3001-BLK-S,4.50,9.99,4.2';
                const blob = new Blob([headers + '\n' + example + '\n'], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `variants_template_${templateId.slice(0, 8)}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Download Template CSV
            </button>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={onClose}>
                {csvResult ? 'Done' : 'Cancel'}
              </Button>
              {!csvResult && (
                <Button
                  size="sm"
                  disabled={!csvFile || csvMutation.isPending}
                  onClick={() => csvFile && csvMutation.mutate(csvFile)}
                >
                  {csvMutation.isPending ? 'Importing...' : 'Upload & Import'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
